<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Companies"
    :items="allCompanies || []"
    :headers="headers"
    item-key="Company_SK"
    selection-state-name="selectedCompanySks"
    :loading="allCompaniesLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Company"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Company_Name" label="Company Name" autofocus></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Companies'"
    item-key="Company_SK"
    display-field="Company_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Company"
    :item-name="itemToDelete?.Company_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_COMPANIES from '@/graphql/Company/allCompanies.gql'
import ADD_COMPANY from '@/graphql/Company/addCompany.gql'
import UPDATE_COMPANY from '@/graphql/Company/updateCompany.gql'
import REMOVE_COMPANY from '@/graphql/Company/removeCompany.gql'
import MERGE_COMPANY from '@/graphql/Company/mergeCompany.gql'

const store = useStore()

const {
  result: allCompaniesResult,
  loading: allCompaniesLoading,
} = useQuery(ALL_COMPANIES)
const allCompanies = computed(() => allCompaniesResult.value?.allCompanies)

const headers = [
  { title: '#', key: 'Company_SK', filterType: 'number', align: 'center' },
  { title: 'Name', key: 'Company_Name', filterType: 'text', align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Company' : 'Edit Company')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allCompanies.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addCompany } = useMutation(ADD_COMPANY,
  () => ({
    update: (cache, { data: { addCompany } }) => {
      const { allCompanies } = cache.readQuery({ query: ALL_COMPANIES })
      const newData = { allCompanies: [...allCompanies, addCompany] }
      cache.writeQuery({ query: ALL_COMPANIES, data: newData })
    }
  })
)

const { mutate: updateCompany } = useMutation(UPDATE_COMPANY)

const save = () => {
  if (editedIndex.value > -1) {
    updateCompany({
      UpdateCompanyInput: {
        Company_SK: editedItem.value.Company_SK,
        Company_Name: editedItem.value.Company_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    addCompany({
      AddCompanyInput: {
        Company_Name: editedItem.value.Company_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeCompany } = useMutation(REMOVE_COMPANY)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeCompany({
    Company_SK: item.Company_SK
  }, {
    update: (cache, { data: { removeCompany } }) => {
      const { allCompanies } = cache.readQuery({ query: ALL_COMPANIES })
      const newData = { allCompanies: allCompanies.filter((c) => c.Company_SK !== removeCompany) }
      cache.writeQuery({ query: ALL_COMPANIES, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Company_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeCompany } = useMutation(MERGE_COMPANY)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeCompany({
    MergeCompanyInput: {
      survivor_Company_SK: mergeData.survivor,
      duplicate_Company_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allCompanies } = cache.readQuery({ query: ALL_COMPANIES })
      const newData = {
        allCompanies: allCompanies.filter(company => !mergeData.duplicates.includes(company.Company_SK))
      }
      cache.writeQuery({ query: ALL_COMPANIES, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} companies`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
